import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import SurveyAPI from 'apis/SurveyAPI';
import { excerptKeys } from '../excerpts/queries';
// Function-scope use only (module cycle via transcripts/queries — safe
// because nothing touches the binding at module-eval time).
import { seedExcerptDetails } from '../utils/transcriptCacheUtils';
import { buildParagraphs } from 'utils/ParagraphUtils';
import { useEffect } from 'react';
import { mapExcerptsToParagraphs } from '../utils/excerptMappingUtils';

// Add survey answers to the query keys
export const surveyKeys = {
    all: ['surveys'],
    lists: () => [...surveyKeys.all, 'list'],
    list: (projectId) => [...surveyKeys.lists(), projectId],
    details: () => [...surveyKeys.all, 'detail'],
    detail: (id) => [...surveyKeys.details(), id],
    responses: () => [...surveyKeys.all, 'responses'],
    response: (id) => [...surveyKeys.responses(), id],
    questions: () => [...surveyKeys.all, 'questions'],
    question: (id) => [...surveyKeys.questions(), id],
    // New key for survey questions collection
    surveyQuestions: (surveyId) => [...surveyKeys.all, 'surveyQuestions', surveyId],
    csvFiles: () => [...surveyKeys.all, 'csvFiles'],
    csvFile: (id) => [...surveyKeys.csvFiles(), id],
    questionResponses: () => [...surveyKeys.questions(), 'responses'],
    questionResponse: (id, offset = 0) => [...surveyKeys.questionResponses(), id, offset],
    // New key for survey row excerpts
    responseExcerpts: (responseId) => [...surveyKeys.response(responseId), 'excerpts'],
    // New key for survey question excerpts
    questionExcerpts: (questionId) => [...surveyKeys.question(questionId), 'excerpts'],
    // New key pattern for survey answers
    answers: () => [...surveyKeys.all, 'answers'],
    answer: (id) => [...surveyKeys.answers(), id],
    // New key for survey rows
    surveyRows: (surveyId, filter) => [...surveyKeys.all, 'rows', surveyId, filter || 'all'],
};

// Cache extraction helper function
const extractAndCacheAnswers = (queryClient, data) => {
  // Check for survey_rows with nested answers
  if (data?.survey_rows?.length) {
    data.survey_rows.forEach(row => {
      if (row.answer) {
        // Cache individual answer
        queryClient.setQueryData(
          surveyKeys.answer(row.answer.id), 
          row.answer
        );
      }
    });
  }
  
  // Check for survey_answers array
  if (data?.survey_answers?.length) {
    data.survey_answers.forEach(answer => {
      // Cache individual answer
      queryClient.setQueryData(
        surveyKeys.answer(answer.id), 
        answer
      );
    });
  }
  
  // Return the original data unchanged
  return data;
};

// Modified base query hooks to extract and cache answers
export function useSurveyData(surveyId) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: surveyKeys.detail(surveyId),
    queryFn: () => SurveyAPI.getSurvey(surveyId)
      .then(response => extractAndCacheAnswers(queryClient, response.data)),
    enabled: !!surveyId,
  });
}

// Hook for getting survey questions
export function useSurveyQuestions(surveyId, options = {}) {
  return useQuery({
    queryKey: surveyKeys.surveyQuestions(surveyId),
    queryFn: () => SurveyAPI.getSurveyQuestions(surveyId)
      .then(response => {
        const data = response?.data || {};
        // Sort survey questions by ID within the query function
        if (data?.survey_questions) {
          data.survey_questions = [...data.survey_questions].sort((a, b) => a.id - b.id);
        }
        return data;
      }),
    enabled: !!surveyId,
    ...options
  });
}

// Hook for getting survey rows with filters
export function useSurveyRows(surveyId, filter, options = {}) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: surveyKeys.surveyRows(surveyId, filter),
    queryFn: () => SurveyAPI.getSurveyRows(surveyId, filter)
      .then(response => {
        const data = response?.data || {};

        // Extract and cache answers from survey rows
        extractAndCacheAnswers(queryClient, data);

        return {
          surveyRows: data.survey_rows || [],
          pageNext: data.next !== undefined ? data.next : null
        };
      }),
    enabled: !!surveyId,
    retry: (failureCount, error) => {
      // Don't retry on 404 or 403 errors
      if (error?.response?.status === 404 ||
          error?.response?.status === 403 ||
          error?.status === 404 ||
          error?.status === 403) {
        return false;
      }

      // For other errors, retry up to 3 times
      return failureCount < 3;
    },
    ...options
  });
}

/**
 * Hook for getting survey response excerpts
 *
 * ⚠️ WARNING: Returns FULL excerpt objects with potentially stale codings ⚠️
 * For current excerpt data, use useExcerpt(id) instead.
 * Future refactor: Should only return excerpt IDs.
 */
export function useSurveyResponseExcerpts(responseId, options = {}) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: surveyKeys.responseExcerpts(responseId),
    queryFn: async ({ signal }) => {
      const response = await SurveyAPI.getSurveyRowExcerpts(responseId);
      const excerpts = response?.data?.excerpts || [];

      // The shared seeding primitive: carries the cancelled-fetch guard
      // every seeding site must obey. The coding mutations cancel these
      // excerpt-bearing survey fetches (cancelSurveyExcerptFetches in
      // excerpts/mutations.js), so the guard fires and a late response
      // cannot re-seed pre-mutation codings — the same cancel-then-guard
      // pair that protects the excerpt lists.
      seedExcerptDetails(queryClient, Array.isArray(excerpts) ? excerpts : [], signal);

      return excerpts;
    },
    enabled: !!responseId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options
  });
}

/**
 * Initialize paragraph excerpt mappings for survey answer excerpts
 * @param {number} responseId - The survey response ID
 * @param {Array} surveyAnswers - Array of survey answers to map to
 */
export function useInitializeSurveyExcerptMappings(responseId, surveyAnswers = []) {
  const queryClient = useQueryClient();
  const { data: excerpts } = useSurveyResponseExcerpts(responseId);
  
  useEffect(() => {
    // Skip if we don't have what we need
    if (!responseId || !surveyAnswers.length || !excerpts || !Array.isArray(excerpts)) {
      return;
    }
    
    // For each survey answer that has a body, build paragraph mappings
    surveyAnswers.forEach(answer => {
      if (!answer.body) return; // Skip answers without text content
      
      // Build paragraphs for this answer
      const paragraphs = buildParagraphs(answer.body);
      
      // Find excerpts that belong to this survey answer
      const answerExcerpts = excerpts.filter(
        excerpt => excerpt.transcript_id === answer.id && excerpt.codeable_type === 'SurveyAnswer'
      );

      // Use shared function to map excerpts to paragraphs
      mapExcerptsToParagraphs(paragraphs, answerExcerpts, answer.id, 'SurveyAnswer', queryClient);
    });
  }, [excerpts, surveyAnswers, responseId, queryClient]);
}

export function useSurveyResponse(responseId) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: surveyKeys.response(responseId),
    queryFn: () => SurveyAPI.getSurveyRow(responseId)
      .then(response => extractAndCacheAnswers(queryClient, response.data)),
    enabled: !!responseId,
    retry: (failureCount, error) => {
      // Don't retry on 404 or 403 errors
      if (error?.response?.status === 404 ||
          error?.response?.status === 403 ||
          error?.status === 404 ||
          error?.status === 403) {
        return false;
      }
      
      // For other errors, retry up to 3 times
      return failureCount < 3;
    }    
  });
}

/**
 * Hook for getting survey question excerpts
 *
 * ⚠️ WARNING: Returns FULL excerpt objects with potentially stale codings ⚠️
 * For current excerpt data, use useExcerpt(id) instead.
 * Future refactor: Should only return excerpt IDs.
 */
export function useSurveyQuestionExcerpts(questionId, options = {}) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: surveyKeys.questionExcerpts(questionId),
    queryFn: async ({ signal }) => {
      const response = await SurveyAPI.getSurveyQuestionExcerpts(questionId);
      // The API returns excerpts in the response data
      const excerpts = response?.data?.excerpts || [];

      // Shared seeding primitive — see useSurveyResponseExcerpts above.
      seedExcerptDetails(queryClient, Array.isArray(excerpts) ? excerpts : [], signal);

      return excerpts;
    },
    enabled: !!questionId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...options
  });
}

/**
 * Initialize paragraph excerpt mappings for survey question answers
 * @param {number} questionId - The survey question ID
 * @param {Array} surveyRows - Array of survey rows from the question response
 */
export function useInitializeSurveyQuestionExcerptMappings(questionId, surveyRows = []) {
  const queryClient = useQueryClient();
  const { data: excerpts } = useSurveyQuestionExcerpts(questionId);
  
  useEffect(() => {
    // Skip if we don't have what we need
    if (!questionId || !surveyRows.length || !excerpts || !Array.isArray(excerpts)) {
      return;
    }
    
    // For each survey row that has an answer with body, build paragraph mappings
    surveyRows.forEach(row => {
      const answer = row.answer;
      if (!answer || !answer.body) return; // Skip rows without answer text
      
      // Build paragraphs for this answer
      const paragraphs = buildParagraphs(answer.body);
      
      // Find excerpts that belong to this survey answer
      const answerExcerpts = excerpts.filter(
        excerpt => excerpt.transcript_id === answer.id && excerpt.codeable_type === 'SurveyAnswer'
      );

      // Use shared function to map excerpts to paragraphs
      mapExcerptsToParagraphs(paragraphs, answerExcerpts, answer.id, 'SurveyAnswer', queryClient);
    });
  }, [excerpts, surveyRows, questionId, queryClient]);
}

export function useSurveyQuestionResponses(questionId, offset = 0) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: surveyKeys.questionResponse(questionId, offset),
    queryFn: () => SurveyAPI.getSurveyQuestionResponses(questionId, offset)
      .then(response => extractAndCacheAnswers(queryClient, response?.data || {})),
    enabled: !!questionId,
    retry: (failureCount, error) => {
      // Don't retry on 404 or 403 errors
      if (error?.status === 404 || error?.status === 403) {
        return false;
      }
      
      // For other errors, retry up to 3 times
      return failureCount < 3;
    }
  });
}

export function useSurveyAnswer(answerId) {
  const queryClient = useQueryClient();
  
  // Use React Query's hook with enabled: false for null values
  // This ensures proper hook behavior for React
  const query = useQuery({
    queryKey: surveyKeys.answer(answerId || 'null'),
    queryFn: () => {
      // Only execute this if we have a valid answerId
      if (answerId == null) {
        return null;
      }
      
      // Always check for the most up-to-date cached data
      const cachedData = queryClient.getQueryData(surveyKeys.answer(answerId));
      
      if (cachedData) {
        return cachedData;
      }
      
      // If we don't have the data, return a placeholder
      return null;
    },
    // Only enable if we have a valid answerId
    enabled: answerId != null,
    // Critical settings to prevent refetching
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Set a very long stale time to prevent auto-refetching
    staleTime: 1000 * 60 * 60, // 1 hour
    // Don't remove from cache
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    // Prevent background refetches
    structuralSharing: false,
    // This makes the hook return null immediately when disabled
    placeholderData: null
  });
  
  // Return the query result
  return query;
}

// The rest of your existing hooks can remain unchanged
export function useSurveyQuestion(questionId) {
  return useQuery({
    queryKey: surveyKeys.question(questionId),
    queryFn: () => SurveyAPI.getSurveyQuestion(questionId)
      .then(response => response.data),
    enabled: !!questionId,
  });
}

export function useSurveyList(projectId) {
  return useQuery({
    queryKey: surveyKeys.list(projectId),
    queryFn: () => SurveyAPI.getSurveys(projectId)
      .then(response => ((response?.data || {}).surveys || [])),
    enabled: !!projectId,
  });
}

// Composite hooks can remain mostly unchanged
export function useSurveyWithDependencies({ surveyId, responseId, questionId }) {
  const { 
    data: surveyResponse,
    isLoading: isLoadingResponse 
  } = useSurveyResponse(responseId);

  const {
    data: surveyQuestion,
    isLoading: isLoadingQuestion
  } = useSurveyQuestion(questionId);

  // Determine effective survey ID
  const effectiveSurveyId = surveyId || 
    surveyResponse?.survey_id || 
    surveyQuestion?.survey_id;

  const {
    data: survey,
    isLoading: isLoadingSurvey
  } = useSurveyData(effectiveSurveyId);

  return {
    survey,
    surveyResponse,
    surveyQuestion,
    isLoading: isLoadingSurvey || 
      (!!responseId && isLoadingResponse) || 
      (!!questionId && isLoadingQuestion),
    effectiveSurveyId
  };
}

// Mutations
export function useDeleteSurvey(options = {}) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (surveyId) => SurveyAPI.destroySurvey(surveyId),
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: surveyKeys.lists() });
      if (options.onSuccess) {
        options.onSuccess(...args);
      }
    }
  });
}

export function useCSVFileUpload(csvFileId, projectId, options = {}) {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: surveyKeys.csvFile(csvFileId),
    queryFn: () => SurveyAPI.getCSVFile(csvFileId).then((data) => {
      // Refresh the survey list once processing finishes
      // (v5 removed useQuery onSuccess)
      if (data?.status === 'completed') {
        queryClient.invalidateQueries({ queryKey: surveyKeys.list(projectId) });
      }
      return data;
    }),
    enabled: !!csvFileId && !!projectId,
    refetchInterval: (query) => {
      // If we have an error and it's a 403 or 404, stop polling.
      // HTTPErrors set status as a number (HTTP404Error → 404).
      const errorStatus = query?.state?.fetchFailureReason?.status;
      if (errorStatus === 404 || errorStatus === 403) {
        return false;
      }

      // Otherwise, if data is missing or the status isn't terminal, keep polling
      const data = query?.state?.data;
      if (!data || (data.status !== 'completed' && data.status !== 'failed')) {
        return 10000; // Poll every 10s
      }

      // If completed or failed, also stop
      return false;
    },
    refetchIntervalInBackground: true,
    retry: (failureCount, error) => {
      // Don't retry on 403 or 404 errors
      const status = error?.response?.status || error?.status;
      if (status === 404 || status === 403) {
        return false;
      }

      // For other errors, retry up to 6 times
      return failureCount < 6;
    },
    ...options
  });
}

export function useUpdateSurveyQuestion(options = {}) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ questionId, questionType }) => 
      SurveyAPI.updateSurveyQuestion(questionId, questionType),
    onSuccess: (result, variables) => {
      const { questionId } = variables;
      
      // Invalidate the specific question query
      queryClient.invalidateQueries({ 
        queryKey: surveyKeys.question(questionId) 
      });
      
      // If we have a surveyId available, also invalidate the survey questions
      // You might need to modify this based on how you want to handle the surveyId
      if (options.surveyId) {
        queryClient.invalidateQueries({ 
          queryKey: surveyKeys.surveyQuestions(options.surveyId) 
        });
      }
      
      // Call any additional onSuccess callback
      if (options.onSuccess) {
        options.onSuccess(result, variables);
      }
    },
    onError: (error, variables) => {
      // Call any additional onError callback
      if (options.onError) {
        options.onError(error, variables);
      }
    }
  });
}

export function useDeleteSurveyQuestion(options = {}) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (questionId) => SurveyAPI.deleteSurveyQuestion(questionId),
    onSuccess: (result, questionId) => {
      // Remove the specific question from cache
      queryClient.removeQueries({ 
        queryKey: surveyKeys.question(questionId) 
      });
      
      // If we have a surveyId available, invalidate the survey questions list
      if (options.surveyId) {
        queryClient.invalidateQueries({ 
          queryKey: surveyKeys.surveyQuestions(options.surveyId) 
        });
      }
      
      // Call any additional onSuccess callback
      if (options.onSuccess) {
        options.onSuccess(result, questionId);
      }
    },
    onError: (error, questionId) => {
      // Call any additional onError callback
      if (options.onError) {
        options.onError(error, questionId);
      }
    }
  });
}